/**
 * Type / assembly / material / level commands (Phase 1 needs: create + update; deletes
 * are guarded — a referenced record refuses to die).
 */
import type {
  AnnoTypeId, AnnoTypeRecord, AssemblyId, AssemblyLayer, AssemblyRecord, CategoryId, LevelId,
  MaterialId, ParamValue, PhaseId, TypeId,
} from '../types'
import { newAnnoTypeId, newAssemblyId, newLevelId, newMaterialId, newPhaseId, newTypeId } from '../ids'
import {
  getAnnoType, getLevel, listAnnoTypes, listAssemblies, listDrafting, listElements, listLevels, listMaterials,
  listPhases, listTypes, listViews, maps, recordToYMap, transact, type ModelDoc,
} from '../doc'
import { getCategory, type CategorySchema } from '../registry'
import { resolveSchemaParams, schemaParams } from '../params'
import { deriveTypeName, NAME_RECIPE_CATEGORIES } from '../typeNaming'
import { CORE_DECLARED_CATEGORIES, CORE_MATERIAL_SPECS, conformCoreLayersFor } from '../coreDefinition'
import * as topoCommands from './topo'
import { datumOffsetFt } from '../datums'

// ---------- AUTO TYPE NAMING + MARKS (casework/naming round, 2026-08-09) ----------
// See typeNaming.ts for the recipes + the firm-library trap this must respect.

/**
 * Materialize a recipe-covered category's name from RAW type params (+ its assembly,
 * for the assembly-backed categories) — the ONE call every type-param write routes
 * through (createType/updateType/setTypeParam), and the same function
 * BimFirmLibraryEditor.jsx calls for the firm-library curation path. Returns null for
 * a category with no recipe — callers keep whatever name they were given.
 */
export function computeTypeName(
  model: ModelDoc,
  category: CategoryId,
  params: Record<string, ParamValue>,
  assemblyId?: AssemblyId,
): string | null {
  if (!NAME_RECIPE_CATEGORIES.has(category)) return null
  const cat = getCategory(category)
  if (!cat) return null
  const { params: resolved } = resolveSchemaParams(cat.typeParams, params)
  // CORE DEFINITION: only an EXPLICIT raw declaration names the core from params —
  // an undeclared wall/floor type (custom/unmigrated) must keep naming from its
  // actual core material, not the schema's wood default. Mirrors conformWallCore.
  if (CORE_DECLARED_CATEGORIES.has(category) && params.coreType === undefined) delete resolved.coreType
  const assembly = assemblyId ? listAssemblies(model).find((a) => a.id === assemblyId) ?? null : null
  return deriveTypeName(category, resolved, assembly, listMaterials(model))
}

/**
 * AUTO TYPE NAMING backfill: recompute and store the recipe-derived name for every
 * EXISTING type in a recipe-covered category, in ONE transaction. Idempotent — a type
 * whose stored name already matches its own current recipe output is a no-op write.
 * Run at doc-open, BEFORE ensureDefaultLibrary (seed.ts) — that function's existence
 * checks compare against the CURRENT recipe output, so a model whose recipe-covered
 * types still carry their pre-recipe manual names must catch up first, or ensureType
 * would see no match and duplicate the seed. Supersedes the old one-off
 * renameTypeIfExists migration hack for every category this now covers.
 */
export function materializeTypeNames(model: ModelDoc): number {
  const fixes: { id: TypeId; name: string }[] = []
  for (const t of listTypes(model)) {
    const computed = computeTypeName(model, t.category, t.params, t.assemblyId)
    if (computed !== null && computed !== t.name) fixes.push({ id: t.id, name: computed })
  }
  if (fixes.length) {
    transact(model, () => {
      for (const f of fixes) maps(model).types.get(f.id)?.set('name', f.name)
    })
  }
  return fixes.length
}

/** per-category schedule-mark prefixes (AUTO TYPE MARKS) */
const MARK_PREFIX: Record<CategoryId, string> = {
  wall: 'WL', floor: 'FL', roof: 'RF', ceiling: 'CL', door: 'D', window: 'W',
  stair: 'ST', ramp: 'RP', railing: 'RL', fence: 'FN', paving: 'PV', casework: 'CW', object: 'OB',
  column: 'CO', beam: 'BM', topo: 'TP', footing: 'FT', stem: 'SW',
  fixture: 'EF', body: 'GE', pool: 'PL', downspout: 'DS', vine: 'VN', niche: 'NC', awning: 'AW', cupola: 'CU',
}
const MARK_COUNTERS_KEY = 'typeMarkCounters'

/** a category's `typeRef` instance-param keys (FOOTINGS round — deleteType's guard) */
const resolveTypeRefKeys = (cat: CategorySchema): string[] =>
  schemaParams(cat.instanceParams).filter((d) => d.kind === 'typeRef').map((d) => d.key)

// ---------- WALL CORE DEFINITION (2026-08-18) ----------

/**
 * Conform a core-declared type's assembly to its core definition — the write-side
 * half of wallCore.ts/floorCore.ts (dispatched by coreDefinition.ts): the FIRST core
 * layer's material becomes the canonical family material (created on demand — a doc
 * that predates the family still conforms) and, for the derived families, its
 * thickness becomes the selected size's ACTUAL width/depth. Runs inside
 * createType/updateType/setTypeParam's own transaction, BEFORE the name recompute, so
 * the materialized name always describes the conformed layers. No-op for every other
 * category and for a type with no assembly.
 */
function conformWallCore(model: ModelDoc, m: import('yjs').Map<unknown>): void {
  const category = m.get('category') as CategoryId
  if (!CORE_DECLARED_CATEGORIES.has(category)) return
  const asmId = m.get('assemblyId') as AssemblyId | undefined
  if (!asmId) return
  const asm = listAssemblies(model).find((a) => a.id === asmId)
  if (!asm) return
  const cat = getCategory(category)
  if (!cat) return
  const params: Record<string, ParamValue> = {}
  ;(m.get('params') as import('yjs').Map<unknown>).forEach((v, k) => { params[k] = v as ParamValue })
  // Only an EXPLICIT core declaration conforms — a type with no raw `coreType`
  // (a custom/legacy record the migration hasn't stamped, or a programmatic fixture
  // with its own exotic core) must never have its core silently rewritten to the
  // schema default. wallCoreMigration.ts / floorCoreMigration.ts stamp declarations.
  if (params.coreType === undefined) return
  const { params: resolved } = resolveSchemaParams(cat.typeParams, params)
  const next = conformCoreLayersFor(category, resolved, asm.layers, asm.coreBoundary, (name) => {
    const existing = listMaterials(model).find((mat) => mat.name === name)
    if (existing) return existing.id
    const spec = CORE_MATERIAL_SPECS[name]
    if (!spec) return null
    return createMaterial(model, {
      name: spec.name, color: spec.color, category: spec.category,
      ...(spec.cut ? { cut: spec.cut } : {}),
    })
  })
  if (next) updateAssembly(model, asm.id, { layers: next })
}

/**
 * Monotonic per-category counter, stored once in `settings` — assigned at type
 * creation and never reshuffled (docs/MODELER.md "AUTO TYPE MARKS"): a type's mark is
 * set here once and nothing else in the app ever recomputes or reassigns it.
 */
function nextTypeMark(model: ModelDoc, category: CategoryId): string {
  const { settings } = maps(model)
  const counters = { ...((settings.get(MARK_COUNTERS_KEY) as Record<string, number>) || {}) }
  const n = (counters[category] || 0) + 1
  counters[category] = n
  settings.set(MARK_COUNTERS_KEY, counters)
  return `${MARK_PREFIX[category]}${String(n).padStart(2, '0')}`
}

// ---------- types ----------

export function createType(
  model: ModelDoc,
  input: { category: CategoryId; name: string; assemblyId?: AssemblyId; params?: Record<string, ParamValue> },
): TypeId {
  const id = newTypeId()
  transact(model, () => {
    const params = input.params ?? {}
    maps(model).types.set(id, recordToYMap({
      id, category: input.category, name: input.name,
      ...(input.assemblyId ? { assemblyId: input.assemblyId } : {}),
      params,
      mark: nextTypeMark(model, input.category),
    }))
    const m = maps(model).types.get(id)!
    // WALL CORE DEFINITION: conform the assembly's core layer BEFORE naming — the
    // materialized name must describe the conformed layers.
    conformWallCore(model, m)
    // AUTO TYPE NAMING: recipe-covered categories never keep the caller's manual
    // name — computed from the SAME params/assembly being stored, so it's correct
    // from the moment the record exists (no separate write-then-fixup).
    const name = computeTypeName(model, input.category, params, input.assemblyId)
    if (name !== null) m.set('name', name)
  })
  return id
}

/**
 * Clone a type under a new name (2.6 polish — the Revit "Edit Type → Duplicate" move:
 * type params like a door's width/height deliberately affect every instance, so making
 * one different means making a new type first). Returns the new type id.
 *
 * Wall-type-simplification round (docs/MODELER.md): the assembly is CLONED too — a
 * type's layers are private to it (the type↔assembly relationship is 1:1 and invisible
 * in the UI), so a duplicate must never share its source's layer stack.
 *
 * Firm-library round: duplicates never carry `firmId` (createType/createAssembly don't
 * set it) — "duplicate to customize" is exactly how a firm standard becomes a badged
 * PROJECT-LOCAL record.
 */
export function duplicateType(model: ModelDoc, id: TypeId, name: string): TypeId | null {
  const src = listTypes(model).find((t) => t.id === id)
  if (!src) return null
  let assemblyId = src.assemblyId
  if (assemblyId) {
    const asm = listAssemblies(model).find((a) => a.id === assemblyId)
    if (asm) {
      assemblyId = createAssembly(model, {
        name,
        kind: asm.kind,
        layers: asm.layers.map((l) => ({ ...l })),
        coreBoundary: { ...asm.coreBoundary },
      })
    }
  }
  return createType(model, {
    category: src.category,
    name,
    assemblyId,
    params: { ...src.params },
  })
}

export function renameType(model: ModelDoc, id: TypeId, name: string): boolean {
  const m = maps(model).types.get(id)
  if (!m) return false
  transact(model, () => { m.set('name', name) })
  return true
}

/**
 * Patch a type's editable fields wholesale (the type editor commits its staged session
 * in ONE call — one transact = one undo step, the updateAssembly contract; per-key
 * setTypeParam calls would each be their own undo step under captureTimeout 0).
 *
 * Wall-type-simplification round: `layers`/`coreBoundary` edit the type's PRIVATE
 * assembly directly (the editor shows them inline — "assembly" is not a user concept).
 * Copy-on-write: if another type still shares the assembly (legacy duplicates), the
 * edit forks a private copy first so a layer change can never reach across types.
 * A rename also renames the private (unshared) assembly so the data stays legible.
 */
export function updateType(
  model: ModelDoc,
  id: TypeId,
  patch: {
    name?: string; assemblyId?: AssemblyId; params?: Record<string, ParamValue>
    layers?: AssemblyLayer[]; coreBoundary?: { start: number; end: number }
  },
): boolean {
  const m = maps(model).types.get(id)
  if (!m) return false
  transact(model, () => {
    if (patch.name !== undefined) m.set('name', patch.name)
    if (patch.assemblyId !== undefined) m.set('assemblyId', patch.assemblyId)
    if (patch.params) {
      const pm = m.get('params') as import('yjs').Map<unknown>
      for (const [k, v] of Object.entries(patch.params)) {
        if (v !== undefined) pm.set(k, v)
      }
    }
    const asmId = m.get('assemblyId') as AssemblyId | undefined
    const asm = asmId ? listAssemblies(model).find((a) => a.id === asmId) : undefined
    if (asm) {
      const name = m.get('name') as string
      const layerPatch = patch.layers !== undefined || patch.coreBoundary !== undefined
      const shared = listTypes(model).some((t) => t.id !== id && t.assemblyId === asm.id)
      if (layerPatch && shared) {
        // fork before writing — the other type keeps the original untouched
        const forkId = createAssembly(model, {
          name,
          kind: asm.kind,
          layers: (patch.layers ?? asm.layers).map((l) => ({ ...l })),
          coreBoundary: { ...(patch.coreBoundary ?? asm.coreBoundary) },
        })
        m.set('assemblyId', forkId)
      } else {
        if (layerPatch) {
          updateAssembly(model, asm.id, {
            ...(patch.layers !== undefined ? { layers: patch.layers } : {}),
            ...(patch.coreBoundary !== undefined ? { coreBoundary: patch.coreBoundary } : {}),
          })
        }
        if (patch.name !== undefined && !shared && asm.name !== patch.name) {
          updateAssembly(model, asm.id, { name: patch.name })
        }
      }
    }
    // WALL CORE DEFINITION: conform the (possibly just-forked) assembly's core layer
    // to the settled params BEFORE the name recompute below.
    conformWallCore(model, m)
    // AUTO TYPE NAMING: recompute LAST, from whatever params/assembly this session
    // just settled on — overrides any manual patch.name for recipe-covered
    // categories (their Name field is read-only in every editor, so patch.name never
    // actually arrives from the UI for them; this is the safety net for any other
    // caller, e.g. tests, that still passes one).
    const category = m.get('category') as CategoryId
    const finalAssemblyId = m.get('assemblyId') as AssemblyId | undefined
    const finalParams: Record<string, ParamValue> = {}
    ;(m.get('params') as import('yjs').Map<unknown>).forEach((v, k) => { finalParams[k] = v as ParamValue })
    const computed = computeTypeName(model, category, finalParams, finalAssemblyId)
    if (computed !== null) m.set('name', computed)
  })
  return true
}

export function setTypeParam(model: ModelDoc, id: TypeId, key: string, value: ParamValue): boolean {
  const m = maps(model).types.get(id)
  if (!m) return false
  transact(model, () => {
    const pm = m.get('params') as import('yjs').Map<unknown>
    pm.set(key, value)
    // WALL CORE DEFINITION: a core param write conforms the assembly first.
    conformWallCore(model, m)
    // AUTO TYPE NAMING: recompute on every type-param write (see the note above).
    const category = m.get('category') as CategoryId
    const assemblyId = m.get('assemblyId') as AssemblyId | undefined
    const params: Record<string, ParamValue> = {}
    pm.forEach((v, k) => { params[k] = v as ParamValue })
    const computed = computeTypeName(model, category, params, assemblyId)
    if (computed !== null) m.set('name', computed)
  })
  return true
}

export function setTypeAssembly(model: ModelDoc, id: TypeId, assemblyId: AssemblyId): boolean {
  const m = maps(model).types.get(id)
  if (!m) return false
  transact(model, () => { m.set('assemblyId', assemblyId) })
  return true
}

/**
 * Refuses when any element uses the type — as its own type, OR through a `typeRef`
 * instance param (FOOTINGS round: a wall's footingTypeId/stemTypeId — the guarded-
 * delete convention, a referenced record never deletes out from under a consumer).
 * Deleting a type also deletes its private assembly when no other type references it
 * (wall-type-simplification round: the assembly is invisible 1:1 data — an orphan
 * would be unreachable from any UI).
 */
export function deleteType(model: ModelDoc, id: TypeId): boolean {
  const typeRefKeys = new Map<string, string[]>()   // category -> typeRef param keys
  for (const el of listElements(model)) {
    if (el.typeId === id) return false
    let keys = typeRefKeys.get(el.category)
    if (keys === undefined) {
      const cat = getCategory(el.category)
      keys = cat ? resolveTypeRefKeys(cat) : []
      typeRefKeys.set(el.category, keys)
    }
    for (const k of keys) if (el.params?.[k] === id) return false
  }
  const { types, assemblies } = maps(model)
  const m = types.get(id)
  if (!m) return false
  const asmId = m.get('assemblyId') as AssemblyId | undefined
  transact(model, () => {
    types.delete(id)
    if (asmId && !listTypes(model).some((t) => t.assemblyId === asmId)) {
      assemblies.delete(asmId)
    }
  })
  return true
}

// ---------- annotation types (r23) ----------
// Text notes and dimensions reference these for their graphic parameters, mirroring
// element types: library records, curated firm-standard set (seeded), duplicated —
// never redefined ad hoc — to customize. Same guarded-delete convention as the rest
// of the library: a type a drafting record still references refuses to die.

/** built-in defaults — what a record with NO typeId renders as (pre-r23 parity).
 *  `refFace: 'core'` (DIMENSIONS SUITE) — structure dimensions to structure. */
export const ANNO_DEFAULTS = { textIn: 3 / 32, accuracyIn: 1 / 64, gapIn: 1 / 16, refFace: 'core' } as const

export function createAnnoType(
  model: ModelDoc,
  input: {
    kind: AnnoTypeRecord['kind']; name: string; textIn: number; accuracyIn?: number; gapIn?: number
    refFace?: AnnoTypeRecord['refFace']
  },
): AnnoTypeId {
  const id = newAnnoTypeId()
  transact(model, () => {
    maps(model).annoTypes.set(id, recordToYMap({
      id, kind: input.kind, name: input.name, textIn: input.textIn,
      ...(input.accuracyIn != null ? { accuracyIn: input.accuracyIn } : {}),
      ...(input.gapIn != null ? { gapIn: input.gapIn } : {}),
      ...(input.refFace != null ? { refFace: input.refFace } : {}),
    }))
  })
  return id
}

/** Patch-merge editable fields (never `id`/`kind`); `null` clears an optional field. */
export function updateAnnoType(
  model: ModelDoc,
  id: AnnoTypeId,
  patch: Partial<Pick<AnnoTypeRecord, 'name' | 'textIn' | 'accuracyIn' | 'gapIn' | 'refFace'>>,
): boolean {
  const m = maps(model).annoTypes.get(id)
  if (!m) return false
  transact(model, () => {
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined) continue
      if (v === null) m.delete(k)
      else m.set(k, v)
    }
  })
  return true
}

/** Clone under a new name — the deliberate "duplicate to customize" move. */
export function duplicateAnnoType(model: ModelDoc, id: AnnoTypeId, name: string): AnnoTypeId | null {
  const src = getAnnoType(model, id)
  if (!src) return null
  return createAnnoType(model, {
    kind: src.kind, name, textIn: src.textIn, accuracyIn: src.accuracyIn, gapIn: src.gapIn,
    refFace: src.refFace,
  })
}

/** Refuses while any drafting record references the type. */
export function deleteAnnoType(model: ModelDoc, id: AnnoTypeId): boolean {
  if (listDrafting(model).some((r) => r.typeId === id)) return false
  const { annoTypes } = maps(model)
  if (!annoTypes.has(id)) return false
  transact(model, () => { annoTypes.delete(id) })
  return true
}

/**
 * A drafting record's RESOLVED annotation style — the referenced type's parameters
 * over the built-in defaults, so a record with no type (every pre-r23 record) renders
 * byte-identical to before types existed. ONE resolution shared by every renderer,
 * hit test, and grip layout.
 */
export function annoStyleFor(
  model: ModelDoc,
  rec: { typeId?: AnnoTypeId | null } | null | undefined,
): { textIn: number; accuracyIn: number; gapIn: number; refFace: NonNullable<AnnoTypeRecord['refFace']> } {
  const t = rec?.typeId ? getAnnoType(model, rec.typeId) : null
  return {
    // never under the 3/32" drawing floor, whatever an old or imported type stored (2026-10-19)
    textIn: Math.max(ANNO_DEFAULTS.textIn, t?.textIn ?? ANNO_DEFAULTS.textIn),
    // MODEL PRECISION round (Matt, 2026-10-08): dimensions never round — they read
    // the real length to 1/64", the model's own lattice. A type's stored accuracyIn
    // is history; it no longer coarsens the text.
    accuracyIn: ANNO_DEFAULTS.accuracyIn,
    gapIn: t?.gapIn ?? ANNO_DEFAULTS.gapIn,
    refFace: t?.refFace ?? ANNO_DEFAULTS.refFace,
  }
}

/** The seeded default type for a kind — smallest text size first (listAnnoTypes order). */
export function defaultAnnoTypeId(model: ModelDoc, kind: AnnoTypeRecord['kind']): AnnoTypeId | null {
  return listAnnoTypes(model).find((t) => t.kind === kind)?.id ?? null
}

// ---------- assemblies ----------

export function createAssembly(
  model: ModelDoc,
  input: { name: string; kind: AssemblyRecord['kind']; layers: AssemblyLayer[]; coreBoundary: { start: number; end: number } },
): AssemblyId {
  const id = newAssemblyId()
  transact(model, () => {
    maps(model).assemblies.set(id, recordToYMap({ id, ...input }))
  })
  return id
}

/**
 * Replace an assembly's editable fields wholesale (the assembly editor commits the whole
 * layer stack at once — one undo step for one editor session commit).
 */
export function updateAssembly(
  model: ModelDoc,
  id: AssemblyId,
  patch: Partial<Pick<AssemblyRecord, 'name' | 'layers' | 'coreBoundary'>>,
): boolean {
  const m = maps(model).assemblies.get(id)
  if (!m) return false
  transact(model, () => {
    if (patch.name !== undefined) m.set('name', patch.name)
    if (patch.layers !== undefined) m.set('layers', patch.layers)
    if (patch.coreBoundary !== undefined) m.set('coreBoundary', patch.coreBoundary)
  })
  return true
}

/** Refuses when any type references the assembly. */
export function deleteAssembly(model: ModelDoc, id: AssemblyId): boolean {
  if (listTypes(model).some((t) => t.assemblyId === id)) return false
  const { assemblies } = maps(model)
  if (!assemblies.has(id)) return false
  transact(model, () => { assemblies.delete(id) })
  return true
}

// ---------- materials ----------
// P6-clean (docs/bim-plan/08 Part 0): the surface was create-only — a materials
// ORGANIZER needs the full lifecycle. P6a widens MaterialRecord itself; these
// commands are shape-agnostic (patch/copy whatever fields the record carries).

export function createMaterial(
  model: ModelDoc,
  input: {
    name: string; color: string; cutPattern?: string
    // v2 fields (P6a MaterialRecord — the topo round's site materials seed these
    // directly so their plan/section hatches are right from birth)
    category?: string
    surface?: { color: string }
    cut?: { pattern: string; fgColor: string; bgColor: string }
  },
): MaterialId {
  const id = newMaterialId()
  transact(model, () => {
    maps(model).materials.set(id, recordToYMap({ id, ...input }))
  })
  return id
}

/** Patch-merge editable material fields (never `id`). One transact = one undo step. */
export function updateMaterial(
  model: ModelDoc,
  id: MaterialId,
  patch: Record<string, unknown>,
): boolean {
  const m = maps(model).materials.get(id)
  if (!m) return false
  transact(model, () => {
    for (const [k, v] of Object.entries(patch)) {
      if (k === 'id' || v === undefined) continue
      m.set(k, v)
    }
  })
  return true
}

export function renameMaterial(model: ModelDoc, id: MaterialId, name: string): boolean {
  return updateMaterial(model, id, { name })
}

/** Clone under a new name — the organizer's Duplicate (the duplicateType pattern).
 *  Never carries `firmId`: the copy is a project-local record by construction. */
export function duplicateMaterial(model: ModelDoc, id: MaterialId, name: string): MaterialId | null {
  const m = maps(model).materials.get(id)
  if (!m) return null
  const copy: Record<string, unknown> = {}
  m.forEach((v, k) => { if (k !== 'id' && k !== 'firmId') copy[k] = v })
  const newId = newMaterialId()
  transact(model, () => {
    maps(model).materials.set(newId, recordToYMap({ ...copy, id: newId, name }))
  })
  return newId
}

/**
 * Refuses while any assembly layer references the material (the deleteAssembly/
 * deleteType guard pattern — a library row never deletes out from under a consumer).
 */
export function deleteMaterial(model: ModelDoc, id: MaterialId): boolean {
  for (const asm of listAssemblies(model)) {
    if (asm.layers.some((l) => l.materialId === id)) return false
  }
  const { materials } = maps(model)
  if (!materials.has(id)) return false
  transact(model, () => { materials.delete(id) })
  return true
}

// ---------- levels ----------

export function createLevel(
  model: ModelDoc,
  input: { name: string; elevationFt: number; order?: number; airtableLevelId?: string },
): LevelId {
  const id = newLevelId()
  transact(model, () => {
    const levels = maps(model).levels
    let maxOrder = -1
    levels.forEach((m) => { const o = m.get('order') as number; if (o > maxOrder) maxOrder = o })
    levels.set(id, recordToYMap({
      id, name: input.name, elevationFt: input.elevationFt,
      order: input.order ?? maxOrder + 1,
      ...(input.airtableLevelId ? { airtableLevelId: input.airtableLevelId } : {}),
    }))
  })
  return id
}

export function updateLevel(
  model: ModelDoc,
  id: LevelId,
  patch: Partial<{ name: string; elevationFt: number; order: number; airtableLevelId: string | null }>,
): boolean {
  const m = maps(model).levels.get(id)
  if (!m) return false
  transact(model, () => {
    // AREA PLAN COMPLETION (docs/bim-plan/28 round 6): the link to the Cortex Concept Level the
    // area totals are sent to — set from the Send Area Totals window; null clears it
    if (patch.airtableLevelId !== undefined) {
      if (patch.airtableLevelId) m.set('airtableLevelId', patch.airtableLevelId)
      else m.delete('airtableLevelId')
    }
    if (patch.name !== undefined) m.set('name', patch.name)
    if (patch.elevationFt !== undefined) m.set('elevationFt', patch.elevationFt)
    if (patch.order !== undefined) m.set('order', patch.order)
  })
  return true
}

/**
 * Re-stamp every level's `order` from its ELEVATION (levels-as-datums round): the
 * stratification IS the truth, so the level dropdown (which reads `order` via
 * listLevels) can never disagree with the Level Manager's section-style stacking.
 * Call after any elevation edit; a no-op when the two already agree.
 */
export function renumberLevelsByElevation(model: ModelDoc): void {
  const sorted = listLevels(model).slice().sort((a, b) => a.elevationFt - b.elevationFt)
  if (sorted.every((l, i) => l.order === i)) return
  transact(model, () => {
    const levels = maps(model).levels
    sorted.forEach((l, i) => { levels.get(l.id)?.set('order', i) })
  })
}

/**
 * Set the STORY HEIGHT between `levelId` and the level above it — the datum method's
 * primary edit. Stories stack: the level above moves to `levelId + heightFt` and every
 * level above THAT moves by the same delta, so raising one floor-to-floor never
 * silently squashes the story above it. One transaction = one undo step. Refuses on
 * the topmost level (no story above to size) or a non-positive height.
 */
export function setStoryHeight(model: ModelDoc, levelId: LevelId, heightFt: number): boolean {
  if (!(heightFt > 0)) return false
  const sorted = listLevels(model).slice().sort((a, b) => a.elevationFt - b.elevationFt)
  const i = sorted.findIndex((l) => l.id === levelId)
  if (i < 0 || i + 1 >= sorted.length) return false
  const delta = (sorted[i].elevationFt + heightFt) - sorted[i + 1].elevationFt
  if (Math.abs(delta) < 1e-9) return true
  transact(model, () => {
    const levels = maps(model).levels
    for (let k = i + 1; k < sorted.length; k++) {
      levels.get(sorted[k].id)?.set('elevationFt', sorted[k].elevationFt + delta)
    }
  })
  return true
}

/**
 * RE-ZERO round (2026-08-20, Matt: "how can I reset the project level?"): shifts EVERY
 * level's elevationFt by a constant so `homeLevelId` lands exactly at 0' project-
 * relative — a pure translation of the model's whole Z origin. Every story height
 * (a DIFFERENCE between elevations) is untouched, and so is every level's REAL
 * elevation: the site datum's own `realElevationFt` is left alone, and since
 * `offset = realElevationFt - home.elevationFt`, home.elevationFt landing on 0 makes
 * the offset become exactly `realElevationFt` — precisely the compensation needed for
 * every level's derived real value to read the same as before the shift.
 *
 * Cascades to the other things that store an ABSOLUTE project-relative Z, so nothing
 * detaches from the (now-shifted) levels: topo survey points + grading modifier
 * elevations (types.ts#TopoPoint — never level-relative, the terrain's whole point)
 * and blue-tape punch notes (`TapeNoteRecord.worldPt` — "sticks where you put it,
 * never moves with the element", so it doesn't move on its own here either unless
 * shifted explicitly). Every other element (walls, floors, doors…) resolves its z as
 * `level.elevationFt + relativeOffset`, so it moves automatically with its own level —
 * nothing else to do there.
 *
 * KNOWN GAP, flagged rather than guessed at: a saved 3D view's camera position/target
 * and any drafting record (dimension/text/spot-elevation tag) placed IN a section or
 * elevation view can also store an absolute Z of their own (confirmed for the latter —
 * annotation/datums.js plots a level's datum line at its raw `elevationFt` inside that
 * view) and would need this same delta to stay visually attached. Not covered here;
 * check for those on a project with real annotation history before relying on this.
 */
export function rezeroLevels(model: ModelDoc, homeLevelId: LevelId): boolean {
  const home = getLevel(model, homeLevelId)
  if (!home) return false
  const delta = -home.elevationFt
  if (Math.abs(delta) < 1e-9) return true
  transact(model, () => {
    const levels = maps(model).levels
    for (const l of listLevels(model)) levels.get(l.id)?.set('elevationFt', l.elevationFt + delta)
    shiftSiteAnchoredZ(model, delta)
    const tapeNotes = maps(model).tapeNotes
    tapeNotes.forEach((m) => {
      const p = m.get('worldPt') as [number, number, number] | undefined
      if (Array.isArray(p) && p.length === 3) m.set('worldPt', [p[0], p[1], p[2] + delta])
    })
  })
  return true
}

/**
 * SURVEY-FIXED round (2026-09-29, Matt: "those elevations in the topo terrain are set
 * to real-world coordinates off the survey … if I change the home level's real world
 * elevation, the home's levels themselves should move up and down relative to the
 * topo terrain"). Everything anchored to the SURVEY rather than the building — the
 * topo's points and grading modifiers, and a stair end tied to a site elevation —
 * shifted by `delta` project-relative feet. Storage stays project-relative (the
 * types.ts topo-revert doctrine); callers use this to hold REAL-WORLD values fixed.
 */
export function shiftSiteAnchoredZ(model: ModelDoc, delta: number): void {
  if (Math.abs(delta) < 1e-9) return
  transact(model, () => {
    for (const el of listElements(model)) {
      if (el.category === 'topo') {
        const points = topoCommands.topoPoints(model, el.id)
        if (points.length) {
          topoCommands.updateTopoPoints(model, el.id, points.map((p) => ({ id: p.id, zFt: p.zFt + delta })))
        }
        for (const m of topoCommands.topoModifiers(model, el.id)) {
          const patch: { elevFt?: number; zs?: number[] } = {}
          if (typeof m.elevFt === 'number') patch.elevFt = m.elevFt + delta
          if (Array.isArray(m.zs)) patch.zs = m.zs.map((z) => z + delta)
          if (patch.elevFt !== undefined || patch.zs !== undefined) {
            topoCommands.updateTopoModifier(model, el.id, m.id, patch)
          }
        }
      } else if (el.category === 'stair') {
        const pm = maps(model).elements.get(el.id)?.get('params') as import('yjs').Map<unknown> | undefined
        if (!pm) continue
        for (const [mode, key] of [['baseMode', 'baseSiteElevationFt'], ['topMode', 'topSiteElevationFt']] as const) {
          const v = pm.get(key)
          if (pm.get(mode) === 'site' && typeof v === 'number') pm.set(key, v + delta)
        }
      }
    }
  })
}

/**
 * SURVEY-FIXED round: run a level/datum edit so the SURVEY stays where it is. Whatever
 * `fn` does to the site datum offset (the home level's real-world elevation, or the
 * home level's own project elevation moving), survey-anchored data shifts the other
 * way so its real-world elevations read exactly as before — the building moves
 * relative to the terrain, never the terrain with the building. One transaction.
 * A FIRST-TIME datum (none before) shifts nothing: terrain drawn before a datum
 * existed was drawn against the building (the Smith Residence lesson).
 */
export function withSurveyFixed(model: ModelDoc, fn: () => void): void {
  transact(model, () => {
    const before = datumOffsetFt(model)
    fn()
    const after = datumOffsetFt(model)
    if (before !== null && after !== null) shiftSiteAnchoredZ(model, -(after - before))
  })
}

/**
 * Refuses when any element sits on the level. GL3 (the recorded deferral, closed):
 * room PINS and separators are not elements, so they never block the delete —
 * instead they clean up IN THE SAME TRANSACTION: the level's placed rooms return
 * to unplaced (only the kernel pin is removed — the Concept room in Airtable is
 * NEVER touched, the identity model) and its separators delete with it. One
 * transaction = one undo step restores the level with its pins and separators.
 */
export function deleteLevel(model: ModelDoc, id: LevelId): boolean {
  if (listElements(model).some((e) => e.levelId === id)) return false
  const { levels, rooms, roomSeparators } = maps(model)
  if (!levels.has(id)) return false
  // collect first, delete second — never mutate a Y.Map mid-iteration
  const roomKeys: string[] = []
  rooms.forEach((m, key) => { if (m.get('levelId') === id) roomKeys.push(key) })
  const sepKeys: string[] = []
  roomSeparators.forEach((m, key) => { if (m.get('levelId') === id) sepKeys.push(key) })
  transact(model, () => {
    for (const k of roomKeys) rooms.delete(k)
    for (const k of sepKeys) roomSeparators.delete(k)
    levels.delete(id)
  })
  return true
}

// ---------- phases (docs/bim-plan/09 §7) ----------
// An ORDERED list, like levels — but with no elevation to derive order from, so order
// is the stored truth here and `reorderPhases` re-stamps it explicitly (the level
// manager's up/down buttons call it after computing the swapped order).

export function createPhase(model: ModelDoc, input: { name: string; order?: number }): PhaseId {
  const id = newPhaseId()
  transact(model, () => {
    const phases = maps(model).phases
    let maxOrder = -1
    phases.forEach((m) => { const o = m.get('order') as number; if (o > maxOrder) maxOrder = o })
    phases.set(id, recordToYMap({ id, name: input.name, order: input.order ?? maxOrder + 1 }))
  })
  return id
}

export function renamePhase(model: ModelDoc, id: PhaseId, name: string): boolean {
  const m = maps(model).phases.get(id)
  if (!m) return false
  transact(model, () => { m.set('name', name) })
  return true
}

/** Re-stamp every phase's `order` from the given sequence, in ONE transaction. */
export function reorderPhases(model: ModelDoc, orderedIds: PhaseId[]): void {
  transact(model, () => {
    const phases = maps(model).phases
    orderedIds.forEach((id, i) => { phases.get(id)?.set('order', i) })
  })
}

/** Swap a phase with its neighbor above/below in the ordered list — the Phase Manager's up/down buttons. */
export function movePhase(model: ModelDoc, id: PhaseId, direction: 'up' | 'down'): boolean {
  const sorted = listPhases(model)
  const i = sorted.findIndex((p) => p.id === id)
  const j = direction === 'up' ? i - 1 : i + 1
  if (i < 0 || j < 0 || j >= sorted.length) return false
  const ids = sorted.map((p) => p.id)
  ;[ids[i], ids[j]] = [ids[j], ids[i]]
  reorderPhases(model, ids)
  return true
}

/** Refuses while any element references the phase (created/demolished) or any view is set to it. */
export function deletePhase(model: ModelDoc, id: PhaseId): boolean {
  if (listElements(model).some((e) => e.phaseCreated === id || e.phaseDemolished === id)) return false
  if (listViews(model).some((v) => v.phase === id)) return false
  const { phases } = maps(model)
  if (!phases.has(id)) return false
  transact(model, () => { phases.delete(id) })
  return true
}
